/**
 * Little synthesised blips — no audio files to download, and nothing plays
 * until the first tap, which is what browsers insist on anyway.
 */
type Blip = "tap" | "good" | "bad" | "win" | "lose" | "join";

const NOTES: Record<Blip, { freq: number[]; length: number; type: OscillatorType }> = {
	tap: { freq: [520], length: 0.07, type: "triangle" },
	good: { freq: [660, 880], length: 0.12, type: "sine" },
	bad: { freq: [220, 165], length: 0.16, type: "sawtooth" },
	win: { freq: [523, 659, 784, 1047], length: 0.14, type: "sine" },
	lose: { freq: [392, 330, 262], length: 0.18, type: "sine" },
	join: { freq: [440, 660], length: 0.1, type: "triangle" },
};

const MUTE_KEY = "playpal.muted";

let context: AudioContext | null = null;
let muted = readMuted();

function readMuted(): boolean {
	try {
		return localStorage.getItem(MUTE_KEY) === "1";
	} catch {
		return false;
	}
}

export function isMuted(): boolean {
	return muted;
}

export function setMuted(next: boolean): void {
	muted = next;
	try {
		localStorage.setItem(MUTE_KEY, next ? "1" : "0");
	} catch {
		// Fine — the setting just won't stick between visits.
	}
}

/** Plays a short sequence of pitches — used by the ear-training rounds. */
export function playTones(frequencies: number[], noteLength = 0.55): void {
	if (muted) return;
	try {
		context ??= new AudioContext();
		if (context.state === "suspended") void context.resume();
		const audio = context;

		frequencies.forEach((freq, index) => {
			if (!Number.isFinite(freq) || freq <= 0) return;
			const start = audio.currentTime + index * (noteLength + 0.15);
			const oscillator = audio.createOscillator();
			const gain = audio.createGain();
			oscillator.type = "sine";
			oscillator.frequency.value = freq;
			gain.gain.setValueAtTime(0.0001, start);
			gain.gain.exponentialRampToValueAtTime(0.16, start + 0.03);
			gain.gain.exponentialRampToValueAtTime(0.0001, start + noteLength);
			oscillator.connect(gain).connect(audio.destination);
			oscillator.start(start);
			oscillator.stop(start + noteLength + 0.05);
		});
	} catch {
		// No audio? The question still says which note came first.
	}
}

export function play(blip: Blip): void {
	if (muted) return;
	try {
		context ??= new AudioContext();
		if (context.state === "suspended") void context.resume();

		const note = NOTES[blip];
		note.freq.forEach((freq, index) => {
			const start = context!.currentTime + index * note.length;
			const oscillator = context!.createOscillator();
			const gain = context!.createGain();
			oscillator.type = note.type;
			oscillator.frequency.value = freq;
			gain.gain.setValueAtTime(0.0001, start);
			gain.gain.exponentialRampToValueAtTime(0.12, start + 0.01);
			gain.gain.exponentialRampToValueAtTime(0.0001, start + note.length);
			oscillator.connect(gain).connect(context!.destination);
			oscillator.start(start);
			oscillator.stop(start + note.length + 0.02);
		});
	} catch {
		// No audio? Not a reason to break the game.
	}
}
